<script>
import { getAuditCase } from '@/services/auditServices'
import { BriefcaseBusinessIcon, ChevronLeft, ChevronRight } from 'lucide-vue-next'
import ImageViewer from '@/components/ImageViewer.vue'
import AuditsSubmitOverlay from './components/AuditsSubmitOverlay.vue'

export default {
  name: 'Audits',
  components: {
    BriefcaseBusinessIcon,
    ChevronLeft,
    ChevronRight,
    ImageViewer,
    AuditsSubmitOverlay
  },
  data() {
    return {
      startDate: '',
      endDate: '',
      images: [],
      loading: false,
      hasSearched: false,
      currentPage: 1,
      limit: 10,
      audit_status: false,
      audit_image: true,
      totalCases: 0,
      totalPages: 0,
      showImageViewer: false,
      selectedImage: {},
      selectedMedia: {},
      showAuditOverlay: false,
      errorMessage: '',
    }
  },
  mounted() {
    const today = new Date()
    this.endDate = today.toISOString().split('T')[0]

    const sevenDaysAgo = new Date(today)
    sevenDaysAgo.setDate(today.getDate() - 7)
    this.startDate = sevenDaysAgo.toISOString().split('T')[0]

    this.fetchAuditCases()
  },
  methods: {
    async fetchAuditCases() {
      if (!this.startDate || !this.endDate) {
        this.errorMessage = 'Select both start and end dates.';
        return
      }

      this.loading = true
      this.hasSearched = true
      this.errorMessage = ''

      const adjustedEndDate = new Date(this.endDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

      try {
        const result = await getAuditCase(
          new Date(this.startDate),
          adjustedEndDate,
          this.audit_status,
          this.audit_image,
          this.currentPage,
          this.limit
        )

        this.images = (result.images || []).reverse()
        this.totalCases = result.total_cases || 0
        this.totalPages = result.total_pages || 0
      } catch (error) {
        console.error('Error fetching audit cases:', error)
        this.images = []
        this.totalCases = 0
        this.totalPages = 0
        this.errorMessage = error.response?.data?.detail === 'No images found for the specified criteria'
          ? ''
          : 'Unable to load audit cases. Please try again.';
      } finally {
        this.loading = false
      }
    },
    async changePage(newPage) {
      this.currentPage = Math.min(Math.max(newPage, 1), Math.max(this.totalPages, 1))
      await this.fetchAuditCases()
    },
    convertToMalaysiaTime(utcDateTime) {
      const utcDate = new Date(`${utcDateTime}Z`);
      const malaysiaDate = new Date(utcDate.getTime() + 8 * 60 * 60 * 1000);
      return malaysiaDate.toISOString().replace('T', ' ').slice(0, 19);
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const convertedDate = this.convertToMalaysiaTime(dateString)
      return new Date(convertedDate).toLocaleString()
    },
    getAIStatusClass(status) {
      return {
        success: status === 'normal',
        danger: status === 'abnormal',
        warning: status === 'non retinal image',
        muted: !status,
      }
    },
    getAuditStatusClass(grade) {
      return grade ? 'success' : 'warning';
    },
    formatGrade(grade) {
      return grade ? grade.replace('_', ' ') : 'Pending audit';
    },
    handleAuditSubmit(image){
      this.selectedMedia = image
      this.showAuditOverlay = true
    },
    closeAuditOverlay() {
      this.showAuditOverlay = false
      this.selectedMedia = null
    },
    handleAuditSubmitted() {
      this.fetchAuditCases()
    }
  },
}
</script>

<template>
  <div class="admin-page wide">
    <ImageViewer
      v-model:show="showImageViewer"
      :image-url="selectedImage.SignedUrl"
      :width="selectedImage.Width"
      :height="selectedImage.Height"
    />

    <AuditsSubmitOverlay
      v-if="showAuditOverlay"
      :audit_data="selectedMedia"
      @close="closeAuditOverlay"
      @audit-submitted="handleAuditSubmitted"
    />

    <div class="page-hero">
      <div>
        <p class="page-kicker">Clinical Review</p>
        <h1 class="page-title"><BriefcaseBusinessIcon :size="26" /> Audit cases</h1>
        <p class="page-subtitle">Review AI-screened media, inspect confidence, and submit or update audit decisions.</p>
      </div>
      <button @click="fetchAuditCases" class="ui-button primary" :disabled="loading">
        <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
        {{ loading ? 'Loading...' : 'Search cases' }}
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Total cases</span>
        <strong class="stat-value">{{ totalCases }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Showing</span>
        <strong class="stat-value">{{ images.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Page</span>
        <strong class="stat-value stat-text">{{ currentPage }} of {{ Math.max(totalPages, 1) }}</strong>
      </div>
    </div>

    <div v-if="errorMessage" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
    </div>

    <div class="ui-card module-filter-card">
      <div class="form-grid three">
        <div class="form-section">
          <label class="field-label">Start date</label>
          <input type="date" v-model="startDate" class="ui-input">
        </div>
        <div class="form-section">
          <label class="field-label">End date</label>
          <input type="date" v-model="endDate" class="ui-input">
        </div>
        <div class="form-section">
          <label class="field-label">Items per page</label>
          <select v-model="limit" class="ui-select" @change="currentPage = 1">
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
        </div>
        <div class="form-section">
          <label class="field-label">Audit status</label>
          <select v-model="audit_status" class="ui-select" @change="currentPage = 1">
            <option :value="null">All</option>
            <option :value="true">Audited</option>
            <option :value="false">Unaudited</option>
          </select>
        </div>
      </div>
    </div>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th>Image</th>
            <th>Media</th>
            <th>AI analysis</th>
            <th>Audit status</th>
            <th>Details</th>
            <th class="text-end">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6">
              <div class="empty-state">Loading audit cases...</div>
            </td>
          </tr>
          <tr v-else v-for="image in images" :key="image.MediaUuid">
            <td data-label="Image">
              <div class="thumbnail-wrap">
                <img :src="image.SignedUrl" class="thumbnail" :alt="image.FileName">
                <button class="zoom-button" @click="showImageViewer = true; selectedImage = image">
                  <i class="fas fa-search-plus"></i>
                </button>
              </div>
            </td>
            <td data-label="Media">
              <span class="table-primary">{{ image.MediaUuid?.substring(0, 8) }}...{{ image.MediaUuid?.slice(-4) }}</span>
              <span class="table-secondary">{{ image.FileName || 'No filename' }}</span>
            </td>
            <td data-label="AI analysis">
              <span class="badge-soft" :class="getAIStatusClass(image.AIAbnormality)">
                {{ image.AIAbnormality || 'No AI result' }}
              </span>
              <div class="confidence-bar">
                <div class="confidence-fill" :style="{ width: `${(image.AIConfidence || 0) * 100}%` }"></div>
              </div>
              <span class="table-secondary">{{ ((image.AIConfidence || 0) * 100).toFixed(1) }}% confidence</span>
            </td>
            <td data-label="Audit status">
              <span class="badge-soft" :class="getAuditStatusClass(image.AuditGrade)">
                {{ formatGrade(image.AuditGrade) }}
              </span>
            </td>
            <td data-label="Details">
              <span class="table-primary">{{ formatDate(image.created_at) }}</span>
              <span class="table-secondary">Brand: {{ image.InstrumentBrand || '—' }}</span>
              <span class="table-secondary">Eye: {{ image.EyeType === 0 ? 'Left' : 'Right' }}</span>
            </td>
            <td class="text-end" data-label="Action">
              <button class="ui-button secondary compact" @click="handleAuditSubmit(image)">
                <i :class="image.AuditAbnormality ? 'fa fa-eye' : 'fas fa-pen'"></i>
                {{ image.AuditAbnormality ? 'View' : 'Audit' }}
              </button>
            </td>
          </tr>
          <tr v-if="!loading && images.length === 0">
            <td colspan="6">
              <div class="empty-state">No audit cases found for the current filters.</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination-row" v-if="totalPages > 1">
        <span class="table-secondary">Page {{ currentPage }} of {{ totalPages }}</span>
        <div class="table-actions">
          <button class="icon-button-soft" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
            <ChevronLeft :size="16" />
          </button>
          <button class="icon-button-soft" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
            <ChevronRight :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.module-filter-card {
  padding: 20px;
  margin-bottom: 18px;
}

.thumbnail-wrap {
  position: relative;
  width: 108px;
}

.thumbnail {
  width: 108px;
  height: 74px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid var(--ui-border);
}

.zoom-button {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
}

.confidence-bar {
  width: 140px;
  height: 7px;
  margin: 10px 0 4px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.confidence-fill {
  height: 100%;
  border-radius: inherit;
  background: var(--primary-color);
}

.table-actions {
  display: flex;
  gap: 10px;
}

.compact {
  min-height: 34px;
  padding-inline: 12px;
  font-size: 0.8rem;
}

.stat-text {
  font-size: 1rem;
}
</style>
